import { useEffect, useRef } from 'react'

import {
  buildLocalDateString,
  clearLegacyStreakState,
  computeNextStreakState,
  readLegacyStreakState,
} from '../_data/renter-streak'
import { defaultQuickPassStreakState } from '../_defaults/quickpass-profile'
import { QuickPassStreakState } from '../_types/quickpass-profile'
import { useAuth } from './use-auth'
import { useQuickPassProfile } from './use-quickpass-profile'

/**
 * Records the renter's visit on first mount after auth + profile resolve
 * and returns the resulting streak state directly from the profile.
 *
 * On the first profile load that has an empty streakState, any legacy
 * device-local streak (from before the streak moved onto the profile) is
 * folded forward and the legacy localStorage key is cleared.
 *
 * @returns {QuickPassStreakState | null} the streak state, or null when signed out
 */
const useRenterStreak = (): QuickPassStreakState | null => {
  const { quickPassUser } = useAuth()
  const { profile, patchProfile } = useQuickPassProfile()
  const lastRecordedKeyReference = useRef<string>('')

  useEffect(() => {
    if (quickPassUser === null || profile === null) {
      return
    }

    const todayString = buildLocalDateString(Date.now())
    const recordedKey = `${quickPassUser.id}:${todayString}`

    if (lastRecordedKeyReference.current === recordedKey) {
      return
    }

    const profileStreak = profile.streakState ?? defaultQuickPassStreakState
    const legacy = readLegacyStreakState(quickPassUser.id)

    const baseStreak: QuickPassStreakState =
      profileStreak.lastVisitDateString === '' && legacy !== null
        ? legacy
        : profileStreak

    const nextStreak = computeNextStreakState(baseStreak, todayString)

    if (legacy !== null) {
      clearLegacyStreakState(quickPassUser.id)
    }

    lastRecordedKeyReference.current = recordedKey

    if (
      nextStreak.lastVisitDateString === profileStreak.lastVisitDateString &&
      nextStreak.currentStreakDays === profileStreak.currentStreakDays &&
      nextStreak.longestStreakDays === profileStreak.longestStreakDays
    ) {
      return
    }

    void patchProfile({ streakState: nextStreak })
  }, [quickPassUser, profile, patchProfile])

  if (profile === null) {
    return null
  }

  return profile.streakState ?? defaultQuickPassStreakState
}

export { useRenterStreak }
